import { useEffect } from 'react';
import { FileText, Crown, Users, Calendar, Flag, BookOpen } from 'lucide-react';
import FlagShowcase from '../components/FlagShowcase';

const HeWhakaputanga = () => {
  useEffect(() => {
    document.title = 'He Whakaputanga | He Whakaputanga nga Hapū o te Tairawhiti';
    return () => {
      document.title = 'He Whakaputanga nga Hapū o te Tairawhiti | Customary Rights Platform';
    };
  }, []);

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header Section */}
      <section className="bg-gradient-to-r from-ocean-teal to-ocean-deep text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            He Whakaputanga
          </h1>
          <p className="text-xl md:text-2xl max-w-4xl mx-auto opacity-90">
            He Whakaputanga o te Rangatiratanga o Nu Tireni - The Declaration of Independence of the United Tribes of New Zealand, 1835
          </p>
          <p className="text-lg max-w-3xl mx-auto mt-4 opacity-80">
            Ko te Wakaminenga o nga Hapu o Nu Tirini - The foundational constitutional document establishing Māori sovereignty
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="card bg-ocean-blue bg-opacity-10 mb-12">
              <div className="flex items-start">
                <Flag className="h-8 w-8 text-ocean-teal mr-4 mt-1" />
                <div>
                  <h2 className="text-2xl font-bold mb-4 text-gray-800">What is He Whakaputanga?</h2>
                  <p className="text-lg text-gray-700 leading-relaxed mb-4">
                    He Whakaputanga o te Rangatiratanga o Nu Tireni (The Declaration of Independence of New Zealand) 
                    was signed on 28 October 1835 by 34 northern rangatira, eventually signed by 52 chiefs. It declared the sovereign independence 
                    of New Zealand under the collective authority of Māori chiefs as "Ko te Wakaminenga o nga Hapu o Nu Tirini" - 
                    The United Tribes of New Zealand.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    This declaration established New Zealand as an independent sovereign nation with international recognition, 
                    including acknowledgment from King William IV and witnessed by representatives from America, France, Australia, and England 
                    with a 21-gun salute to the Māori sovereign nation.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="card">
                <Calendar className="h-8 w-8 text-ocean-teal mb-4" />
                <h3 className="text-xl font-semibold mb-3 text-gray-800">Key Date</h3>
                <p className="text-gray-600">
                  Signed on 28 October 1835, five years before Te Tiriti o Waitangi. 
                  This makes it New Zealand's first constitutional document and the foundation of Māori sovereignty. 
                  The original document is held in the Court of St. James in England.
                </p>
              </div>

              <div className="card">
                <Users className="h-8 w-8 text-ocean-teal mb-4" />
                <h3 className="text-xl font-semibold mb-3 text-gray-800">The Signatories</h3>
                <p className="text-gray-600">
                  Initially signed by 34 northern rangatira on 28 October 1835, eventually reaching 52 signatures 
                  from chiefs representing various iwi and hapū. The chiefs acknowledged other chiefs of New Zealand 
                  and asked that all chiefs unite as one, putting aside differences for the benefit of all Māori.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Provisions */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="section-header text-center mb-12">Key Provisions</h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              <div className="cultural-border">
                <h3 className="text-xl font-semibold mb-3 text-ocean-teal">1. Declaration of Independence</h3>
                <p className="text-gray-600 mb-3">
                  "We, the hereditary chiefs and heads of the tribes of the Northern parts of New Zealand... 
                  do hereby declare the Independence of our country."
                </p>
                <p className="text-gray-600">
                  This established New Zealand as an independent nation under Māori sovereignty.
                </p>
              </div>

              <div className="cultural-border">
                <h3 className="text-xl font-semibold mb-3 text-ocean-teal">2. Collective Authority</h3>
                <p className="text-gray-600 mb-3">
                  "All sovereign power and authority within the territories of the United Tribes of New Zealand 
                  is hereby declared to reside entirely and exclusively in the hereditary chiefs and heads of tribes."
                </p>
                <p className="text-gray-600">
                  This affirmed that political authority rested with Māori rangatira.
                </p>
              </div>

              <div className="cultural-border">
                <h3 className="text-xl font-semibold mb-3 text-ocean-teal">3. Legislative Assembly</h3>
                <p className="text-gray-600 mb-3">
                  The Declaration established that the chiefs would meet annually in congress to frame laws 
                  for the dispension of justice and the preservation of peace and good order.
                </p>
                <p className="text-gray-600">
                  This created New Zealand's first parliament - a Māori parliament.
                </p>
              </div>

              <div className="cultural-border">
                <h3 className="text-xl font-semibold mb-3 text-ocean-teal">4. Protection from Foreign Interference</h3>
                <p className="text-gray-600">
                  The Declaration stated that no legislative authority would be acknowledged 
                  unless chosen by the assembled chiefs of the United Tribes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Historical Significance */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="section-header text-center mb-12">Historical Significance</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card">
              <Crown className="h-8 w-8 text-ocean-teal mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-gray-800">Constitutional Foundation</h3>
              <p className="text-gray-600 mb-4">
                He Whakaputanga established the constitutional and legal foundation of New Zealand 
                as an independent nation under Māori authority.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>First constitutional document</li>
                <li>Established Māori sovereignty</li>
                <li>Created governmental structures</li>
              </ul>
            </div>

            <div className="card">
              <FileText className="h-8 w-8 text-ocean-teal mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-gray-800">International Recognition</h3>
              <p className="text-gray-600 mb-4">
                The Declaration was formally acknowledged by the British government and 
                sent to other nations for recognition.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>British acknowledgment</li>
                <li>International notification</li>
                <li>Diplomatic recognition</li>
              </ul>
            </div>

            <div className="card">
              <BookOpen className="h-8 w-8 text-ocean-teal mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-gray-800">Legal Precedence</h3>
              <p className="text-gray-600 mb-4">
                As it predates the Treaty of Waitangi by five years, He Whakaputanga 
                provides important context for understanding Treaty negotiations.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Pre-Treaty sovereignty</li>
                <li>Legal continuity</li>
                <li>Constitutional precedent</li>
              </ul>
            </div>

            <div className="card">
              <Users className="h-8 w-8 text-ocean-teal mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-gray-800">Modern Relevance</h3>
              <p className="text-gray-600 mb-4">
                He Whakaputanga continues to be relevant in contemporary discussions 
                about Māori sovereignty and constitutional arrangements.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Sovereignty movements</li>
                <li>Constitutional debates</li>
                <li>Legal arguments</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* The Full Text */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="section-header text-center mb-12">Understanding the Declaration Today</h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="card bg-ocean-blue bg-opacity-10">
              <h3 className="text-xl font-semibold mb-4 text-ocean-deep">Contemporary Significance</h3>
              <p className="text-gray-700 leading-relaxed">
                He Whakaputanga remains a living document that affirms Māori as the first peoples 
                of Aotearoa with inherent rights to self-determination. It provides a constitutional 
                foundation that predates colonial governance and continues to inform discussions 
                about indigenous rights, sovereignty, and the relationship between Māori and the Crown.
              </p>
            </div>

            <div className="mt-8 grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <h4 className="font-semibold text-gray-800 mb-2">Legal Foundation</h4>
                <p className="text-gray-600 text-sm">
                  Provides legal basis for ongoing sovereignty claims
                </p>
              </div>
              <div className="text-center">
                <h4 className="font-semibold text-gray-800 mb-2">Cultural Identity</h4>
                <p className="text-gray-600 text-sm">
                  Affirms Māori identity as a sovereign people
                </p>
              </div>
              <div className="text-center">
                <h4 className="font-semibold text-gray-800 mb-2">Political Rights</h4>
                <p className="text-gray-600 text-sm">
                  Supports claims to self-governance and tino rangatiratanga
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Flag Showcase */}
      <FlagShowcase />
    </div>
  );
};

export default HeWhakaputanga;
